import { IValueSetOptionsFilter } from "models/options/options.model";

/**
 * Query keys for a versioned master-data entity. `versions(id)` is the prefix of
 * every `version(id, n)` key so all snapshots of one entity can be dropped at once.
 */
const createVersionedEntityKeys = (entity: string) => {
  const all = [entity] as const;
  const lists = () => [...all, "list"] as const;
  const details = () => [...all, "detail"] as const;
  const versions = (id: string) => [...all, "version", id] as const;
  return {
    all,
    lists,
    list: (params: Record<string, unknown>) => [...lists(), params] as const,
    details,
    detail: (id: string) => [...details(), id] as const,
    history: (id: string) => [...all, "history", id] as const,
    versions,
    version: (id: string, versionNumber: string) =>
      [...versions(id), versionNumber] as const,
  };
};

export type VersionedEntityKeys = ReturnType<typeof createVersionedEntityKeys>;

export const productPropertiesKeys = {
  all: ["productProperties"] as const,
  lists: () => [...productPropertiesKeys.all, "list"] as const,
  list: (params: Record<string, unknown>) =>
    [...productPropertiesKeys.lists(), params] as const,
  codeOptions: (productType: string | null) =>
    [...productPropertiesKeys.all, "codeOptions", productType] as const,
};

export const valueSetsKeys = createVersionedEntityKeys("valueSets");

export const siteInfoKeys = {
  all: ["siteInformation"] as const,
};

export const widthMatrixKeys = createVersionedEntityKeys("widthMatrix");

export const machineAssignmentKeys =
  createVersionedEntityKeys("machineAssignment");

export const lengthMatrixKeys = createVersionedEntityKeys("lengthMatrix");

export const measurementCorrectionKeys = createVersionedEntityKeys(
  "measurementCorrection",
);

export const knittingSequenceKeys =
  createVersionedEntityKeys("knittingSequence");

export const baseSizeCalculationFormulaKeys = createVersionedEntityKeys(
  "baseSizeCalculationFormula",
);

export const measurementCalculationFormulaKeys = createVersionedEntityKeys(
  "measurementCalculationFormula",
);

export const widthMeasurementInspectionKeys = createVersionedEntityKeys(
  "widthMeasurementInspection",
);

export const pantyTopMeasurementInspectionKeys = createVersionedEntityKeys(
  "pantyTopMeasurementInspection",
);

export const lengthMeasurementInspectionKeys = createVersionedEntityKeys(
  "lengthMeasurementInspection",
);

export const measurementCalculationFactorKeys = createVersionedEntityKeys(
  "measurementCalculationFactor",
);

export const optionsKeys = {
  all: ["optionsKeys"] as const,
  formulaValidationOptions: () =>
    [...optionsKeys.all, "formulaValidationOptions"] as const,
  defaultFormulaTemplates: () =>
    [...optionsKeys.all, "defaultFormulaTemplates"] as const,
  mainProductLines: () => [...optionsKeys.all, "mainProductLine"] as const,
  bodyAreas: () => [...optionsKeys.all, "bodyArea"] as const,
  measurementPointTypes: () =>
    [...optionsKeys.all, "measurementPointTypes"] as const,
  productPropertyTypes: () =>
    [...optionsKeys.all, "productPropertyTypes"] as const,
  valueSets: () => [...optionsKeys.all, "valueSets"],
  valueSetsByType: (
    productPropertyType: string,
    filter: IValueSetOptionsFilter = {},
  ) => [
    ...optionsKeys.valueSets(),
    productPropertyType,
    filter.mainProductLine ?? "",
    filter.bodyArea ?? "",
  ],
  measurementPoints: () => [...optionsKeys.all, "measurementPoints"] as const,
  referenceMeasurementPoints: () =>
    [...optionsKeys.all, "referenceMeasurementPoints"] as const,
  circularKnittingSizes: () =>
    [...optionsKeys.all, "circularKnittingSizes"] as const,
  circularKnittingProgramLibraries: () =>
    [...optionsKeys.all, "circularKnittingProgramLibraries"] as const,
  cylinderSizes: () => [...optionsKeys.all, "cylinderSizes"] as const,
  referencePantyTops: () => [...optionsKeys.all, "referencePantyTops"] as const,
  pantyTopTypes: () => [...optionsKeys.all, "pantyTopTypes"] as const,
  knittingDirections: () => [...optionsKeys.all, "knittingDirections"] as const,
  circularKnittingMachineTypes: () =>
    [...optionsKeys.all, "circularKnittingMachineTypes"] as const,
  measurementTypes: () => [...optionsKeys.all, "measurementTypes"] as const,
};

export const knittingProgramLibraryKeys = {
  all: ["knittingProgramLibrary"] as const,
  lists: () => [...knittingProgramLibraryKeys.all, "list"] as const,
  list: (params: Record<string, unknown>) =>
    [...knittingProgramLibraryKeys.lists(), params] as const,
  options: () => [...knittingProgramLibraryKeys.all, "options"] as const,
  details: () => [...knittingProgramLibraryKeys.all, "detail"] as const,
  detail: (id: string) =>
    [...knittingProgramLibraryKeys.details(), id] as const,
  history: (id: string) =>
    [...knittingProgramLibraryKeys.all, "history", id] as const,
};

export const patternProgramKeys = {
  all: ["patternProgram"] as const,
  detail: (id: string) => [...patternProgramKeys.all, "detail", id] as const,
  lists: () => [...patternProgramKeys.all, "list"] as const,
  list: (params: Record<string, unknown>) =>
    [...patternProgramKeys.lists(), params] as const,
  history: (id: string) => [...patternProgramKeys.all, "history", id] as const,
};

export const miniProgramKeys = {
  all: ["miniProgram"] as const,
  detail: (id: string) => [...miniProgramKeys.all, "detail", id] as const,
  lists: () => [...miniProgramKeys.all, "list"] as const,
  list: (params: Record<string, unknown>) =>
    [...miniProgramKeys.lists(), params] as const,
  history: (id: string) => [...miniProgramKeys.all, "history", id] as const,
};

export const pantyTopProgramKeys = {
  all: ["pantyTopProgram"] as const,
  detail: (id: string) => [...pantyTopProgramKeys.all, "detail", id] as const,
  lists: () => [...pantyTopProgramKeys.all, "list"] as const,
  list: (params: Record<string, unknown>) =>
    [...pantyTopProgramKeys.lists(), params] as const,
  history: (id: string) => [...pantyTopProgramKeys.all, "history", id] as const,
};

export const knittingProgramKeys = {
  all: ["knittingProgram"] as const,
  lists: () => [...knittingProgramKeys.all, "list"] as const,
  list: (params: Record<string, unknown>) =>
    [...knittingProgramKeys.lists(), params] as const,
  history: (id: string) => [...knittingProgramKeys.all, "history", id] as const,
};
